import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Plus, X, Save, Shield, CheckCircle, Disc3, Users, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import LocationAutocomplete from '../components/LocationAutocomplete';
import ImageUpload from '../components/ImageUpload';

import { API_URL } from '../utils/api';

const skillLevels = ['Absolute Beginner', 'Beginner', 'Improver', 'Intermediate', 'Advanced'];
const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AddEditClubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const isAdmin = user?.is_admin;
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    additional_locations: [],
    phone: '',
    email: '',
    website: '',
    schedule: [],
    image_url: '',
    logo_url: '',  // Club logo for ticket emails
    is_featured: false,
    // Verification fields (admin only)
    verified_accredited: false,
    verified_insurance: false,
    verified_ppl: false,
    verified_safeguarding: false,
    // Charity fields
    charity_name: '',
    charity_link: ''
  });

  const [newSchedule, setNewSchedule] = useState({ day: '', time: '', venue: '', levels: [] });
  const [newLocation, setNewLocation] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchClub();
    }
  }, [id]);

  const fetchClub = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clubs/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Handle legacy data: if no location field, build from city/region/country
        if (!data.location && (data.city || data.region || data.country)) {
          data.location = [data.city, data.region, data.country].filter(Boolean).join(', ');
        }
        setFormData(data);
      }
    } catch (error) {
      console.error('Error fetching club:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addScheduleItem = () => {
    if (newSchedule.day && newSchedule.time) {
      // Convert levels array to string for storage (or keep as array)
      const scheduleItem = {
        ...newSchedule,
        level: newSchedule.levels.length > 0 ? newSchedule.levels.join(' & ') : ''
      };
      delete scheduleItem.levels; // Remove the temporary levels array
      
      setFormData(prev => ({
        ...prev,
        schedule: [...prev.schedule, scheduleItem]
      }));
      setNewSchedule({ day: '', time: '', venue: '', levels: [] });
    }
  };

  const removeScheduleItem = (index) => {
    setFormData(prev => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index)
    }));
  };

  const addAdditionalLocation = () => {
    if (newLocation.trim()) {
      setFormData(prev => ({
        ...prev,
        additional_locations: [...(prev.additional_locations || []), { address: newLocation.trim() }]
      }));
      setNewLocation('');
    }
  };

  const removeAdditionalLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_locations: prev.additional_locations.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = isEdit ? `${API_URL}/api/clubs/${id}` : `${API_URL}/api/clubs`;
      const method = isEdit ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('You must be logged in to create a club. Please log in and try again.');
        setLoading(false);
        return;
      }
      
      console.log('Submitting club data:', formData);
      
      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);

      // Read body as text first (Safari-friendly approach)
      let responseText = '';
      try {
        responseText = await response.text();
      } catch (e) {
        console.log('Could not read response body');
      }

      if (response.ok) {
        console.log('Club created/updated successfully');
        toast.success(isEdit ? 'Club updated successfully!' : 'Club created successfully! Check your dashboard.');
        navigate('/dashboard');
      } else {
        let errorMessage = 'Failed to save club';
        if (responseText) {
          try {
            const error = JSON.parse(responseText);
            errorMessage = error.detail || errorMessage;
          } catch (e) {
            // Not JSON
          }
        }
        if (response.status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (response.status === 422) {
          errorMessage = 'Please check all required fields are filled in correctly.';
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Club save error:', error);
      // On Safari, network issues can cause various errors
      // Show a helpful message and redirect to dashboard to check
      toast.warning('Please check your dashboard to see if your club was created.');
      setTimeout(() => navigate('/dashboard'), 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="add-edit-club-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </Button>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Syne, sans-serif' }}>
                {isEdit ? 'Edit Club' : 'Add New Club'}
              </h1>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="save-club-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Club'}
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-6 lg:px-12 py-8">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-8">
          {/* Basic Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Basic Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="name">Club Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter club name"
                  required
                  className="mt-1.5"
                  data-testid="club-name-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your club..."
                  required
                  rows={4}
                  className="mt-1.5"
                  data-testid="club-description-input"
                />
              </div>
            </div>
          </motion.div>

          {/* Club Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Club Image</h2>
            <ImageUpload
              value={formData.image_url}
              onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
              label="Upload Club Photo"
              helpText="Upload a photo of your venue, dance floor, or group"
            />
          </motion.div>

          {/* Club Logo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-2">Club Logo</h2>
            <p className="text-sm text-slate-500 mb-6">
              Your logo will appear on ticket confirmation emails sent to customers
            </p>
            <ImageUpload
              value={formData.logo_url}
              onChange={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
              label="Upload Club Logo"
              helpText="Recommended: Square image, minimum 200x200px (PNG or JPG)"
            />
            {formData.logo_url && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Preview:</p>
                <div className="flex items-center gap-3">
                  <img 
                    src={formData.logo_url} 
                    alt="Club logo preview" 
                    className="w-12 h-12 object-contain rounded-lg border border-slate-200"
                  />
                  <div>
                    <p className="text-xs text-slate-400">Hosted by</p>
                    <p className="text-sm font-semibold text-slate-700">{formData.name || 'Your Club Name'}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Location */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Locations</h2>
            
            {/* Primary Location */}
            <div className="mb-6">
              <Label htmlFor="location">Primary Location *</Label>
              <div className="mt-1.5">
                <LocationAutocomplete
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={(value) => setFormData(prev => ({ ...prev, location: value }))}
                  placeholder="Start typing a UK location..."
                  required
                />
              </div>
              <p className="text-sm text-slate-400 mt-1.5">
                Start typing to see suggestions, or enter a custom location
              </p>
            </div>

            {/* Additional Locations */}
            <div className="border-t border-slate-200 pt-6">
              <Label className="mb-3 block">Additional Locations</Label>
              <p className="text-sm text-slate-400 mb-4">
                If your club operates in multiple venues, add them here
              </p>
              
              {/* Existing additional locations */}
              {formData.additional_locations && formData.additional_locations.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.additional_locations.map((loc, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-4 h-4 text-violet-500 flex-shrink-0" />
                      <span className="flex-1 text-sm">{loc.address}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAdditionalLocation(index)}
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new location */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <LocationAutocomplete
                    id="new-location"
                    name="new-location"
                    value={newLocation}
                    onChange={setNewLocation}
                    placeholder="Add another location..."
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addAdditionalLocation}
                  disabled={!newLocation.trim()}
                  className="flex-shrink-0"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Contact Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="01onal 123456"
                  className="mt-1.5"
                  data-testid="club-phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contact@yourclub.co.uk"
                  className="mt-1.5"
                  data-testid="club-email-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.yourclub.com"
                  className="mt-1.5"
                  data-testid="club-website-input"
                />
              </div>
            </div>
          </motion.div>

          {/* Charity Support (Optional) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-2">Charity Support</h2>
            <p className="text-sm text-slate-400 mb-6">Optionally link a charity your club supports</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="charity_name">Charity Name</Label>
                <Input
                  id="charity_name"
                  name="charity_name"
                  value={formData.charity_name || ''}
                  onChange={handleChange}
                  placeholder="e.g., Hope House Children's Hospice"
                  className="mt-1.5"
                  data-testid="club-charity-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="charity_link">Charity Website</Label>
                <Input
                  id="charity_link"
                  name="charity_link"
                  value={formData.charity_link || ''}
                  onChange={handleChange}
                  placeholder="https://www.charityname.org"
                  className="mt-1.5"
                  data-testid="club-charity-link-input"
                />
              </div>
            </div>
          </motion.div>

          {/* Schedule */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-2">Class Schedule</h2>
            <p className="text-sm text-slate-400 mb-6">Add your class times with location and skill level for each session</p>
            
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block text-sm">Day *</Label>
                  <Select
                    value={newSchedule.day}
                    onValueChange={(value) => setNewSchedule(prev => ({ ...prev, day: value }))}
                  >
                    <SelectTrigger data-testid="schedule-day-select">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {days.map(day => (
                        <SelectItem key={day} value={day}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Time *</Label>
                  <Input
                    value={newSchedule.time}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, time: e.target.value }))}
                    placeholder="e.g., 7:00 PM - 8:00 PM"
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block text-sm">Venue/Location</Label>
                  <Input
                    value={newSchedule.venue}
                    onChange={(e) => setNewSchedule(prev => ({ ...prev, venue: e.target.value }))}
                    placeholder="e.g., Community Hall, Main Street"
                    data-testid="schedule-venue-input"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Skill Level(s)</Label>
                  <div className="flex flex-wrap gap-3 p-3 border border-slate-200 rounded-lg bg-white">
                    {skillLevels.map(level => (
                      <label 
                        key={level} 
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={newSchedule.levels.includes(level)}
                          onCheckedChange={(checked) => {
                            setNewSchedule(prev => ({
                              ...prev,
                              levels: checked 
                                ? [...prev.levels, level]
                                : prev.levels.filter(l => l !== level)
                            }));
                          }}
                          data-testid={`schedule-level-${level.toLowerCase()}`}
                        />
                        <span className={`text-sm font-medium ${
                          level === 'Absolute Beginner' ? 'text-emerald-700' :
                          level === 'Beginner' ? 'text-green-700' :
                          level === 'Improver' ? 'text-yellow-700' :
                          level === 'Intermediate' ? 'text-orange-700' :
                          'text-red-700'
                        }`}>
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Select one or more levels</p>
                </div>
              </div>
              
              <Button
                type="button"
                variant="outline"
                onClick={addScheduleItem}
                className="w-full md:w-auto"
                data-testid="add-schedule-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Class
              </Button>
            </div>
            
            {formData.schedule.length > 0 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-slate-700">Added Classes</Label>
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                  {formData.schedule.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                          <span className="text-slate-400 text-xs block">Day</span>
                          <span className="font-medium text-slate-900">{item.day}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 text-xs block">Time</span>
                          <span className="text-slate-700">{item.time}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 text-xs block">Venue</span>
                          <span className="text-slate-700">{item.venue || '-'}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 text-xs block">Level</span>
                          <div className="flex flex-wrap gap-1">
                            {(item.level || 'All Levels').split(' & ').map((lvl, idx) => (
                              <span 
                                key={idx}
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  lvl === 'Absolute Beginner' ? 'bg-emerald-100 text-emerald-700' :
                                  lvl === 'Beginner' ? 'bg-green-100 text-green-700' :
                                  lvl === 'Improver' ? 'bg-yellow-100 text-yellow-700' :
                                  lvl === 'Intermediate' ? 'bg-orange-100 text-orange-700' :
                                  lvl === 'Advanced' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {lvl}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeScheduleItem(index)}
                        className="ml-3 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        data-testid={`remove-schedule-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Admin Only - Verification Badges */}
          {isAdmin && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl shadow-sm p-6 md:p-8 border border-violet-200"
            >
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-violet-600" />
                <h2 className="text-lg font-bold text-slate-900">Verification Badges</h2>
                <span className="text-xs bg-violet-600 text-white px-2 py-0.5 rounded-full">Admin Only</span>
              </div>
              <p className="text-sm text-slate-600 mb-6">These badges will be displayed on the club's profile once verified.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-200 hover:border-blue-300 cursor-pointer transition-colors">
                  <Checkbox
                    checked={formData.verified_accredited}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, verified_accredited: checked }))}
                    className="mt-0.5"
                    data-testid="verify-accredited"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-slate-900">Accredited Instructor</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Instructor has recognized qualifications</p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-200 hover:border-green-300 cursor-pointer transition-colors">
                  <Checkbox
                    checked={formData.verified_insurance}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, verified_insurance: checked }))}
                    className="mt-0.5"
                    data-testid="verify-insurance"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-slate-900">Insurance</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Valid public liability insurance</p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-200 hover:border-purple-300 cursor-pointer transition-colors">
                  <Checkbox
                    checked={formData.verified_ppl}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, verified_ppl: checked }))}
                    className="mt-0.5"
                    data-testid="verify-ppl"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Disc3 className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-slate-900">PPL License</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">PPL/PRS music license</p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-200 hover:border-orange-300 cursor-pointer transition-colors">
                  <Checkbox
                    checked={formData.verified_safeguarding}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, verified_safeguarding: checked }))}
                    className="mt-0.5"
                    data-testid="verify-safeguarding"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-slate-900">Under 18s Safeguarding</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">DBS checks and safeguarding in place</p>
                  </div>
                </label>
              </div>
            </motion.div>
          )}
        </form>
      </div>

      {/* Floating Save Button - visible on mobile and when scrolled */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg md:hidden z-50">
        <Button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-violet-600 hover:bg-violet-500 py-6 text-lg"
          data-testid="save-club-btn-floating"
        >
          <Save className="w-5 h-5 mr-2" />
          {loading ? 'Saving...' : 'Save Club'}
        </Button>
      </div>
      
      {/* Add padding at the bottom on mobile to account for floating button */}
      <div className="h-24 md:hidden" />
    </div>
  );
}
